import { useHead } from '@vueuse/head';
import { computed, isRef, unref } from 'vue';

const BASE_URL = 'https://frenkel.com.mx';
const DEFAULT_DESCRIPTION = 'Frenkel Engineering impulsa tu transformacion digital con soluciones de Industria 4.0, automatizacion, consultoria y capacitacion industrial.';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'Frenkel Engineering';

export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  robots = 'index, follow',
  type = 'website',
} = {}) {
  const unwrap = (val, fallback = '') => {
    if (isRef(val)) return val;
    return computed(() => unref(val) ?? fallback);
  };

  const normalizeText = (value, fallback = '') => {
    const resolved = typeof value === 'string' ? value.trim() : '';
    return resolved || fallback;
  };

  const reactiveTitle = unwrap(title, '');
  const reactiveDescription = unwrap(description, DEFAULT_DESCRIPTION);
  const reactivePath = unwrap(path, '/');
  const reactiveImage = unwrap(image, DEFAULT_IMAGE);
  const reactiveRobots = unwrap(robots, 'index, follow');
  const reactiveType = unwrap(type, 'website');

  const fullTitle = computed(() => {
    const t = normalizeText(reactiveTitle.value);
    return t ? `${t} | ${SITE_NAME}` : `${SITE_NAME} | Industria 4.0, Automatización y Transformación Digital`;
  });

  const metaDescription = computed(() => normalizeText(reactiveDescription.value, DEFAULT_DESCRIPTION));

  const url = computed(() => {
    const resolvedPath = `${unref(reactivePath.value) ?? '/'}`.trim();
    if (/^https?:\/\//i.test(resolvedPath)) return resolvedPath;
    const normalizedPath = resolvedPath.startsWith('/') ? resolvedPath : `/${resolvedPath}`;
    return `${BASE_URL}${normalizedPath}`;
  });

  useHead({
    title: fullTitle,
    htmlAttrs: {
      lang: 'es',
    },
    meta: [
      { name: 'description', content: metaDescription },
      { name: 'robots', content: reactiveRobots },
      { property: 'og:type', content: reactiveType },
      { property: 'og:url', content: url },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: metaDescription },
      { property: 'og:image', content: reactiveImage },
      { property: 'og:image:alt', content: fullTitle },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: 'es_MX' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:url', content: url },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: metaDescription },
      { name: 'twitter:image', content: reactiveImage },
      { name: 'twitter:image:alt', content: fullTitle },
    ],
    link: [
      { rel: 'canonical', href: url },
    ],
  });
}
